import React from 'react';
import Button from './Button';
import Calendar from 'react-calendar';
import 'react-calendar/dist/Calendar.css';
import TimeSlotGrid from './TimeSlotGrid';
import ViewToggle from './ViewToggle';
import { formatDateLong } from '../lib/utils';
import { formatDateLongInTimezone, formatTimeInTimezone, getBrowserTimezone } from '../utils/timezone';
import { getAnonymousCart } from '../services/cart/anonymousCartService';
import { CheckCircleIcon } from '@heroicons/react/24/solid';

interface TimeSlot {
  windowId: string;
  windowName?: string;
  windowType: 'daily' | 'weekly' | 'monthly';
  startDateTime: string;
  endDateTime: string;
  price: number;
  availableQuantity: number;
  totalQuantity: number;
}

// Reusable slot card component
interface SlotCardProps {
  slot: TimeSlot;
  isSelected: boolean;
  showTimeRange?: boolean;
  timezone: string;
  onClick: () => void;
}

const SlotCard: React.FC<SlotCardProps> = ({ slot, isSelected, showTimeRange = false, timezone, onClick }) => {
  const startDate = new Date(slot.startDateTime);
  const endDate = new Date(slot.endDateTime);
  const isDaily = slot.windowType === 'daily';

  return (
    <div
      className={`border rounded-lg p-4 cursor-pointer transition-colors ${
        isSelected
          ? 'border-primary-600 bg-primary-50 ring-2 ring-primary-600'
          : 'border-gray-300 hover:border-primary-400'
      }`}
      onClick={onClick}
    >
      <div className="flex items-start gap-3">
        {/* Checkbox indicator */}
        <div className="flex-shrink-0 mt-1">
          {isSelected ? (
            <CheckCircleIcon className="w-6 h-6 text-primary-600" />
          ) : (
            <div className="w-6 h-6 rounded-full border-2 border-gray-300" />
          )}
        </div>
        
        <div className="flex-1 flex justify-between items-start">
          <div className="flex-1">
            {slot.windowName && (
              <h3 className="font-semibold text-gray-900 mb-1">{slot.windowName}</h3>
            )}
            <p className="text-base text-gray-900 font-medium">
              {formatDateLongInTimezone(startDate, timezone)}
            </p>
            {showTimeRange && isDaily && (
              <p className="text-sm text-gray-600 mt-1">
                {formatTimeInTimezone(startDate, timezone)} - {formatTimeInTimezone(endDate, timezone)}
              </p>
            )}
            {!isDaily && (
              <p className="text-sm text-gray-600 mt-1 capitalize">
                {slot.windowType} rental
              </p>
            )}
            {slot.availableQuantity > 0 && slot.availableQuantity < slot.totalQuantity && (
              <p className="text-xs text-orange-600 mt-1">
                Only {slot.availableQuantity} left
              </p>
            )}
          </div>
          <div className="text-right ml-4">
            <p className="text-2xl font-bold text-primary-600">
              ${slot.price}
            </p>
            <p className="text-xs text-gray-500">per slot</p>
          </div>
        </div>
      </div>
    </div>
  );
};

interface RentalWindow {
  _id: string;
  name?: string;
  windowType: 'monthly' | 'weekly' | 'daily';
  startTime?: string;
  endTime?: string;
  intervalMinutes?: number;
  repeatDaysOfWeek?: number[];
  specificDate?: string;
  isRepeating: boolean;
  repeatStartDate?: string;
  repeatEndDate?: string;
  repeatExceptions: string[];
  pricePerInterval: number;
  quantity: number;
}

interface AvailabilitySectionProps {
  listing: {
    name: string;
    showCalendar: boolean;
    timezone: string;
  };
  windows: RentalWindow[];
  slots: TimeSlot[];
  selectedDate: Date | null;
  view: 'list' | 'grid';
  hasDailyWindows: boolean;
  selectedSlots: Set<string>;
  isLoadingDateSlots: boolean;
  isLoadingMoreSlots: boolean;
  hasMoreSlots: boolean;
  hasWindowOnDate: (date: Date) => boolean;
  handleDateSelect: (date: Date) => void;
  handleClearDate: () => void;
  setView: (view: 'list' | 'grid') => void;
  toggleSlotSelection: (slot: TimeSlot) => void;
  handlePrevDay: () => void;
  handleNextDay: () => void;
  loadMoreSlots: () => void;
  getSlotKey: (slot: TimeSlot) => string;
  isPreview?: boolean;
}

const AvailabilitySection: React.FC<AvailabilitySectionProps> = ({
  listing,
  windows,
  slots,
  selectedDate,
  view,
  hasDailyWindows,
  selectedSlots,
  isLoadingDateSlots,
  isLoadingMoreSlots,
  hasMoreSlots,
  hasWindowOnDate,
  handleDateSelect,
  handleClearDate,
  setView,
  toggleSlotSelection,
  handlePrevDay,
  handleNextDay,
  loadMoreSlots,
  getSlotKey,
  isPreview = false,
}) => {
  // Separate daily and non-daily slots for mixed view
  const dailySlots = slots.filter(slot => slot.windowType === 'daily');
  const nonDailySlots = slots.filter(slot => slot.windowType !== 'daily');

  // Every slot's start/end instant means whatever it means in the listing's
  // timezone, not the renter's browser timezone — falling back to the
  // browser's own zone only guards against unexpectedly missing data, since
  // `timezone` is a required field on every listing.
  const timezone = listing.timezone || getBrowserTimezone();

  return (
    <>
      {/* Calendar for daily windows */}
      {hasDailyWindows && listing.showCalendar && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-gray-900">Select a Date</h2>
            {selectedDate && (
              <Button
                onClick={handleClearDate}
                variant="secondary"
                size="sm"
              >
                Clear Selection
              </Button>
            )}
          </div>
          <div className="flex justify-center">
            <Calendar
              onChange={(value) => handleDateSelect(value as Date)}
              value={selectedDate}
              minDate={new Date()}
              calendarType="gregory"
              tileDisabled={({ date, view }) => {
                if (view === 'month') {
                  return !hasWindowOnDate(date);
                }
                return false;
              }}
              className="border-0 rounded-lg"
            />
          </div>
          {selectedDate && (
            <div className="mt-4 p-4 bg-primary-50 border border-primary-200 rounded-lg">
              <p className="text-sm text-gray-900">
                {isLoadingDateSlots ? (
                  <>Loading slots for <span className="font-semibold">{formatDateLong(selectedDate)}</span>...</>
                ) : (
                  <>Showing {slots.length} slot{slots.length !== 1 ? 's' : ''} for <span className="font-semibold">{formatDateLong(selectedDate)}</span></>
                )}
              </p>
            </div>
          )}
        </div>
      )}

      {/* View Toggle - only show if listing has daily windows and calendar is enabled */}
      {hasDailyWindows && listing.showCalendar && selectedDate && (
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold text-gray-900">Available Time Slots</h2>
          <ViewToggle currentView={view} onViewChange={setView} />
        </div>
      )}

      {/* Show message when date not selected for daily windows with calendar */}
      {hasDailyWindows && listing.showCalendar && !selectedDate && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {!hasDailyWindows && <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Time Slots</h2>}
          <p className="text-gray-600">Please select a date from the calendar above to view available time slots.</p>
        </div>
      )}
      
      {/* Grid View - daily slots (only with calendar) */}
      {hasDailyWindows && listing.showCalendar && selectedDate && view === 'grid' && (
        <div className="mb-6">
          <TimeSlotGrid
            listingName={listing.name}
            selectedDate={selectedDate}
            windows={windows}
            slots={dailySlots}
            cartItems={isPreview ? [] : getAnonymousCart()}
            timezone={timezone}
            onPrevDay={handlePrevDay}
            onNextDay={handleNextDay}
          />
          {isPreview && (
            <div className="mt-2 bg-yellow-50 border border-yellow-300 rounded-lg p-3">
              <p className="text-yellow-800 text-sm">Preview Mode - Booking is disabled in grid view</p>
            </div>
          )}
        </div>
      )}

      {/* Show non-daily slots below grid if any exist */}
      {hasDailyWindows && selectedDate && view === 'grid' && nonDailySlots.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          <h2 className="text-xl font-bold text-gray-900 mb-4">Other Rental Options</h2>
          <p className="text-sm text-gray-600 mb-3">Tap a slot to select it, then add to cart</p>
          <div className="space-y-3">
            {nonDailySlots.map((slot, index) => (
              <SlotCard
                key={`${slot.windowId}-${index}`}
                slot={slot}
                isSelected={selectedSlots.has(getSlotKey(slot))}
                timezone={timezone}
                onClick={() => toggleSlotSelection(slot)}
              />
            ))}
          </div>
        </div>
      )}

      {/* List View - show when: calendar disabled OR (view is list AND (date selected OR no daily windows)) */}
      {(!listing.showCalendar || ((selectedDate || !hasDailyWindows) && view === 'list')) && (
        <div className="bg-white rounded-lg shadow-sm p-6 mb-6">
          {(!hasDailyWindows || !listing.showCalendar) && <h2 className="text-2xl font-bold text-gray-900 mb-4">Available Time Slots</h2>}
          
          {isLoadingDateSlots ? (
            <p className="text-gray-600">Loading slots...</p>
          ) : slots.length === 0 ? (
            <p className="text-gray-600">
              {selectedDate 
                ? 'No time slots available for the selected date.' 
                : 'No time slots available at this time.'}
            </p>
          ) : (
            <>
              {/* Instruction prompt */}
              <p className="text-sm text-gray-600 mb-3">Tap a slot to select it, then add to cart</p>
              
              <div className="space-y-3">
                {slots.map((slot, index) => (
                  <SlotCard
                    key={`${slot.windowId}-${index}`}
                    slot={slot}
                    isSelected={selectedSlots.has(getSlotKey(slot))}
                    showTimeRange={true}
                    timezone={timezone}
                    onClick={() => toggleSlotSelection(slot)}
                  />
                ))}
            </div>

            {/* Pagination, for the same cases that browse chronologically */}
            {(!listing.showCalendar || !hasDailyWindows) && hasMoreSlots && (
              <div className="mt-4 text-center">
                <Button
                  onClick={loadMoreSlots}
                  isLoading={isLoadingMoreSlots}
                  variant="secondary"
                  className="w-full"
                >
                  Show More
                </Button>
              </div>
            )}
          </>
        )}
      </div>
      )}
    </>
  );
};

export default AvailabilitySection;

